// Reminder Configuration Component
// Allows users to configure reminders for event invitations
import { renderPageTitle } from '../utils/emoji-badge.js';

class ReminderConfig extends HTMLElement {
  constructor() {
    super();
    this.eventId = null;
    this.reminders = [];
    this.loading = false;
    this.isOpen = false;
  }

  connectedCallback() {
    this.eventId = this.getAttribute('event-id');
    if (this.eventId) {
      this.loadReminders();
    }
  }

  async open(eventId) {
    this.eventId = eventId;
    this.isOpen = true;
    this.render();
    await this.loadReminders();
  }

  close() {
    this.isOpen = false;
    this.render();
  }

  async loadReminders() {
    if (!this.eventId) return;

    try {
      this.loading = true;

      const data = await window.API.request(`/events/${this.eventId}/reminders`);
      this.reminders = data.reminders || [];
    } catch (error) {
      // 404 is OK - means not invited
      if (error.statusCode !== 404) {
        window.logger.error('Failed to load reminders:', error);
      }
      this.reminders = [];
    } finally {
      this.loading = false;
      this.render();
    }
  }

  async saveReminders() {
    if (!this.eventId) return;

    try {
      const reminders = this.reminders.map(r => ({
        reminder_offset_minutes: r.reminder_offset_minutes,
        notification_type: r.notification_type
      }));

      await window.API.request(`/events/${this.eventId}/reminders`, {
        method: 'POST',
        body: JSON.stringify({ reminders })
      });

      // Dispatch event on document so event-detail can listen
      document.dispatchEvent(new CustomEvent('reminders-saved', {
        detail: { eventId: this.eventId }
      }));

      this.close();
    } catch (error) {
      alert(error.message || 'Failed to save reminders');
    }
  }

  async deleteReminder(reminderId) {
    if (!reminderId) return;

    if (!confirm('Are you sure you want to delete this reminder?')) {
      return;
    }

    try {
      await window.API.request(`/events/reminders/${reminderId}`, {
        method: 'DELETE'
      });

      this.reminders = this.reminders.filter(r => r.id !== reminderId);
      this.render();
      this.dispatchEvent(new CustomEvent('reminder-deleted', { bubbles: true }));
    } catch (error) {
      alert(error.message || 'Failed to delete reminder');
    }
  }

  addReminder() {
    if (this.reminders.length >= 10) {
      alert('Maximum 10 reminders allowed');
      return;
    }

    this.reminders.push({
      id: null,
      reminder_offset_minutes: 1440, // 1 day default
      notification_type: 'email',
      reminder_time: null,
      sent: false
    });

    this.render();
  }

  updateReminder(index, field, value) {
    if (this.reminders[index]) {
      this.reminders[index][field] = value;

      // If this is an existing reminder, update it on the server
      if (this.reminders[index].id) {
        this.updateReminderOnServer(this.reminders[index].id, field, value);
      }
    }
  }

  async updateReminderOnServer(reminderId, field, value) {
    try {
      const updateData = {};
      updateData[field] = value;

      const data = await window.API.request(`/events/reminders/${reminderId}`, {
        method: 'PUT',
        body: JSON.stringify(updateData)
      });

      // Update local reminder
      const index = this.reminders.findIndex(r => r.id === reminderId);
      if (index !== -1) {
        this.reminders[index] = data.reminder;
        this.render();
      }
    } catch (error) {
      window.logger.error('Failed to update reminder:', error);
    }
  }

  removeReminder(index) {
    const reminder = this.reminders[index];

    if (reminder.id) {
      // Delete from server
      this.deleteReminder(reminder.id);
    } else {
      // Just remove from local array
      this.reminders.splice(index, 1);
      this.render();
    }
  }

  formatOffset(minutes) {
    if (minutes < 60) {
      return `${minutes} minute${minutes !== 1 ? 's' : ''} before`;
    } else if (minutes < 1440) {
      const hours = Math.floor(minutes / 60);
      return `${hours} hour${hours !== 1 ? 's' : ''} before`;
    } else {
      const days = Math.floor(minutes / 1440);
      return `${days} day${days !== 1 ? 's' : ''} before`;
    }
  }

  render() {
    if (!this.isOpen) {
      this.innerHTML = '';
      return;
    }

    this.innerHTML = `
      <div class="reminder-config-overlay" id="reminderOverlay">
        <div class="reminder-config-modal">
          <div class="modal-header">
            ${renderPageTitle('🔔', 'Configure Reminders')}
            <button class="close-btn" id="closeBtn">×</button>
          </div>
          <div class="modal-body">
            ${this.loading ? this.renderLoading() : this.renderContent()}
          </div>
        </div>
      </div>
    `;
    this.attachEventListeners();
  }

  renderLoading() {
    return `
      <div class="loading-container">
        <div class="spinner"></div>
        <p>Loading reminders...</p>
      </div>
    `;
  }

  renderContent() {
    return `
      <div class="reminder-config-content">
        <p class="help-text">
          Set up reminders so you don't forget about this event. 
          You can have up to 10 reminders per event.
        </p>

        <div class="reminders-list" id="remindersList">
          ${this.reminders.map((reminder, index) => this.renderReminderItem(reminder, index)).join('')}
        </div>

        <div class="actions">
          ${this.reminders.length < 10 ? `
            <button class="btn btn-secondary" id="addReminderBtn">
              + Add Reminder
            </button>
          ` : ''}
          <button class="btn btn-primary" id="saveBtn">
            Save Reminders
          </button>
        </div>
      </div>
    `;
  }

  renderReminderItem(reminder, index) {
    const isNew = !reminder.id;

    return `
      <div class="reminder-item ${isNew ? 'new' : ''}" data-index="${index}">
        <div class="reminder-header">
          <h3>Reminder ${index + 1}</h3>
          ${!isNew ? `
            <button class="btn-icon delete-btn" data-index="${index}" title="Delete reminder">
              🗑️
            </button>
          ` : `
            <button class="btn-icon remove-btn" data-index="${index}" title="Remove reminder">
              ×
            </button>
          `}
        </div>

        <div class="reminder-fields">
          <div class="field-group">
            <label>Time Before Event:</label>
            <div class="offset-selector">
              <select class="offset-days" data-index="${index}" data-field="days">
                ${this.renderDaysOptions(reminder.reminder_offset_minutes)}
              </select>
              <select class="offset-hours" data-index="${index}" data-field="hours">
                ${this.renderHoursOptions(reminder.reminder_offset_minutes)}
              </select>
              <select class="offset-minutes" data-index="${index}" data-field="minutes">
                ${this.renderMinutesOptions(reminder.reminder_offset_minutes)}
              </select>
            </div>
            <p class="offset-preview">${this.formatOffset(reminder.reminder_offset_minutes)}</p>
          </div>

          <div class="field-group">
            <label>Notification Type:</label>
            <div class="notification-type-selector">
              <label class="radio-label">
                <input type="radio" name="notification_${index}" value="email" 
                       ${reminder.notification_type === 'email' ? 'checked' : ''}
                       data-index="${index}" data-field="notification_type">
                <span>📧 Email</span>
              </label>
              <label class="radio-label">
                <input type="radio" name="notification_${index}" value="sms" 
                       ${reminder.notification_type === 'sms' ? 'checked' : ''}
                       data-index="${index}" data-field="notification_type">
                <span>📱 SMS</span>
              </label>
            </div>
          </div>

          ${reminder.sent ? `
            <div class="reminder-status">
              <span class="status-badge sent">✅ Sent</span>
              ${reminder.sent_at ? `
                <span class="sent-time">at ${new Date(reminder.sent_at).toLocaleString()}</span>
              ` : ''}
            </div>
          ` : ''}
        </div>
      </div>
    `;
  }

  renderDaysOptions(offsetMinutes) {
    const days = Math.floor(offsetMinutes / 1440);
    let options = '<option value="0">0 days</option>';
    for (let i = 1; i <= 30; i++) {
      options += `<option value="${i}" ${days === i ? 'selected' : ''}>${i} day${i !== 1 ? 's' : ''}</option>`;
    }
    return options;
  }

  renderHoursOptions(offsetMinutes) {
    const remainingMinutes = offsetMinutes % 1440;
    const hours = Math.floor(remainingMinutes / 60);
    let options = '<option value="0">0 hours</option>';
    for (let i = 1; i < 24; i++) {
      options += `<option value="${i}" ${hours === i ? 'selected' : ''}>${i} hour${i !== 1 ? 's' : ''}</option>`;
    }
    return options;
  }

  renderMinutesOptions(offsetMinutes) {
    const minutes = offsetMinutes % 60;
    const options = [0, 15, 30, 45].map(m =>
      `<option value="${m}" ${minutes === m ? 'selected' : ''}>${m} minutes</option>`
    );
    return options.join('');
  }

  calculateOffset(days, hours, minutes) {
    return (parseInt(days) * 1440) + (parseInt(hours) * 60) + parseInt(minutes);
  }

  attachEventListeners() {
    const closeBtn = this.querySelector('#closeBtn');
    if (closeBtn) {
      closeBtn.addEventListener('click', () => this.close());
    }

    const overlay = this.querySelector('#reminderOverlay');
    if (overlay) {
      overlay.addEventListener('click', (e) => {
        if (e.target === overlay) {
          this.close();
        }
      });
    }

    const addBtn = this.querySelector('#addReminderBtn');
    if (addBtn) {
      addBtn.addEventListener('click', () => this.addReminder());
    }

    const saveBtn = this.querySelector('#saveBtn');
    if (saveBtn) {
      saveBtn.addEventListener('click', () => this.saveReminders());
    }

    // Handle offset changes
    const offsetSelects = this.querySelectorAll('.offset-days, .offset-hours, .offset-minutes');
    offsetSelects.forEach(select => {
      select.addEventListener('change', (e) => {
        const index = parseInt(e.target.dataset.index);
        const daysSelect = this.querySelector(`.offset-days[data-index="${index}"]`);
        const hoursSelect = this.querySelector(`.offset-hours[data-index="${index}"]`);
        const minutesSelect = this.querySelector(`.offset-minutes[data-index="${index}"]`);

        const days = parseInt(daysSelect.value) || 0;
        const hours = parseInt(hoursSelect.value) || 0;
        const minutes = parseInt(minutesSelect.value) || 0;

        const totalMinutes = this.calculateOffset(days, hours, minutes);
        this.updateReminder(index, 'reminder_offset_minutes', totalMinutes);
      });
    });

    // Handle notification type changes
    const notificationRadios = this.querySelectorAll('input[type="radio"][data-field="notification_type"]');
    notificationRadios.forEach(radio => {
      radio.addEventListener('change', (e) => {
        const index = parseInt(e.target.dataset.index);
        this.updateReminder(index, 'notification_type', e.target.value);
      });
    });

    // Handle delete/remove buttons
    const deleteBtns = this.querySelectorAll('.delete-btn, .remove-btn');
    deleteBtns.forEach(btn => {
      btn.addEventListener('click', (e) => {
        const index = parseInt(e.target.dataset.index);
        this.removeReminder(index);
      });
    });
  }
}

customElements.define('reminder-config', ReminderConfig);

